import { WorkspacePlan } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Calendar, Mail } from 'lucide-react';
import { useMemo } from 'react';

import {
  BILLING_CALENDLY_URL,
  BILLING_SALES_EMAIL,
} from '@/core/billing/billing-config';
import { redirectToStripe } from '@/core/billing/redirect-to-stripe';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

interface EnterpriseContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional context for the sales email subject/body. */
  interest?: string;
}

export function EnterpriseContactDialog({
  open,
  onOpenChange,
  interest,
}: EnterpriseContactDialogProps) {
  const dict = useDictionary();
  const contactDict = dict.workspace.billing.enterpriseContact;
  const workspace = useCurrentWorkspace();

  const mailtoHref = useMemo(() => {
    const subject = contactDict.emailSubject.replace(
      '{workspace}',
      workspace?.name ?? '—',
    );
    const body = contactDict.emailBody
      .replace('{workspace}', workspace?.name ?? '—')
      .replace('{interest}', interest ?? contactDict.defaultInterest);
    return `mailto:${BILLING_SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [
    contactDict.defaultInterest,
    contactDict.emailBody,
    contactDict.emailSubject,
    interest,
    workspace?.name,
  ]);

  const handleBookCall = async () => {
    await redirectToStripe(BILLING_CALENDLY_URL, { allowOnNative: true });
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{contactDict.title}</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            {contactDict.description}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <Button
            variant="primary"
            className="w-full justify-start gap-2"
            onClick={() => void handleBookCall()}
          >
            <Calendar className="h-4 w-4" />
            {contactDict.bookCall}
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            asChild
          >
            <a href={mailtoHref}>
              <Mail className="h-4 w-4" />
              {contactDict.emailSales}
            </a>
          </Button>
        </div>

        <ResponsiveDialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {contactDict.close}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

export function isEnterpriseOnlyFeature(plan: WorkspacePlan): boolean {
  return plan === WorkspacePlan.ENTERPRISE;
}
